import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Container, ErrorState, LoadingState, PageHeader, Section } from '@/components/common';
import { routes } from '@/config/routes';
import {
  mapPublicCategoryToCategoryCardModel,
  mapPublicProductSummaryToProductCardModel,
} from '@/features/catalog/api/catalogMappers';
import {
  CatalogControls,
  CatalogLoadMore,
  CategoryGrid,
  ProductGrid,
} from '@/features/catalog/components';
import { useCatalog } from '@/features/catalog/context';
import { usePublicCategories, usePublicProducts } from '@/features/catalog/hooks';
import { parseCatalogUrlState, toProductListInput } from '@/features/catalog/utils/catalogUrlState';
import { useDocumentMetadata } from '@/hooks/useDocumentMetadata';

export function ProductsPage() {
  useDocumentMetadata(
    'Personalized Products',
    'Browse personalized wood engraving, ornaments, and custom T-shirts.',
  );
  const [searchParams] = useSearchParams();
  const state = parseCatalogUrlState(searchParams);
  const input = toProductListInput(state);
  const { media } = useCatalog();
  const categoriesQuery = usePublicCategories({ limit: 24 });
  const productsQuery = usePublicProducts(input);
  const categories = (categoriesQuery.data?.items ?? []).map((item) =>
    mapPublicCategoryToCategoryCardModel(item, media),
  );
  const products = useMemo(() => {
    const unique = new Map(
      (productsQuery.data?.pages ?? [])
        .flatMap((page) => page.items)
        .map((item) => [item.id, mapPublicProductSummaryToProductCardModel(item, media)] as const),
    );
    return [...unique.values()];
  }, [media, productsQuery.data]);

  return (
    <>
      <PageHeader
        eyebrow="The Emazy collection"
        title="Find a product worth making personal"
        description="Browse published products with filters backed by the live catalog."
        breadcrumbs={[{ label: 'Home', to: routes.home }, { label: 'Products' }]}
      />
      <Section spacing="compact">
        <Container size="wide">
          {categoriesQuery.isPending && <LoadingState label="Loading product categories" />}
          {categoriesQuery.isSuccess && <CategoryGrid categories={categories} />}
          {categoriesQuery.isError && (
            <ErrorState
              title="Categories could not be loaded"
              onRetry={() => void categoriesQuery.refetch()}
            />
          )}
        </Container>
      </Section>
      <Section spacing="large">
        <Container size="wide" aria-busy={productsQuery.isPending}>
          <CatalogControls
            resultCount={products.length}
            categories={categories}
            updating={productsQuery.isFetching && !productsQuery.isPending}
          />
          {productsQuery.isPending && <LoadingState />}
          {productsQuery.isError && products.length === 0 && (
            <ErrorState
              title="The product catalog is unavailable"
              description="This is a connection problem, not an empty catalog. Please try again."
              onRetry={() => void productsQuery.refetch()}
            />
          )}
          {products.length > 0 && <ProductGrid products={products} />}
          {productsQuery.isSuccess && products.length === 0 && (
            <ProductGrid products={[]} emptyTitle="No products match these filters" />
          )}
          <CatalogLoadMore
            hasMore={Boolean(productsQuery.hasNextPage)}
            loading={productsQuery.isFetchingNextPage}
            onLoadMore={() => void productsQuery.fetchNextPage()}
          />
        </Container>
      </Section>
    </>
  );
}
